



















































































































import { Vue, Component } from "vue-property-decorator";
import { BillingCatalog } from "@/data/models/BillingCatalog";
import { getBillingCatalogDisplayName } from "@/utils/BillingCatalogDisplay";
import { fetchBillingCatalogs, updateBillingCatalog, importBillingCatalogs } from "@/data/repository/BillingCatalogRepository";
import { editDataViewerAccess } from "@/permissions/UserPermissions";
import { getActiveUser } from "@/data/repository/UserRepository";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import router from "@/router/router";
import { ComponentName } from "@/utils/RouteGuards";
import { firestore } from "firebase";

/**
 * Settings sub-page displaying a list of active billing catalogs (Leistungsverzeichnisse)
 * for the current client. Import is handled via Cloud Function (Phase 2).
 */
@Component
export default class BillingCatalogListComponent extends Vue {
  isLoading = true;
  isSyncing = false;
  catalogs: BillingCatalog[] = [];
  dateMenus: Record<string, boolean> = {};

  /**
   * Whether to show the price-source column.
   *
   * It only says anything when more than one X360 tenant has been imported. With a
   * single tenant every row repeats the same value, and a constant column invites the
   * wrong reading: "ABT" on the LVM row means those LVM prices were fetched from the
   * ABT tenant, not that LVM belongs to ABT.
   *
   * It becomes load-bearing as soon as a second company is imported (C3 of the LV
   * plan): two rows will then share an LV name and a PriceClass and differ only by
   * the tenant they were priced in, so the table would be ambiguous without it.
   */
  get showTenantColumn(): boolean {
    return new Set(this.catalogs.map((c) => c.tenant).filter((t) => !!t)).size > 1;
  }

  /** Catalog name for display; translates the no-price-class placeholder. */
  displayName(catalog: BillingCatalog): string {
    return getBillingCatalogDisplayName(catalog);
  }

  /** Table header background, theme-aware so it stays readable in dark mode. */
  get headerColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get canEdit(): boolean {
    return editDataViewerAccess(getActiveUser());
  }

  async mounted() {
    this.setToolbarTitle();
    try {
      this.catalogs = await fetchBillingCatalogs();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Formats a Firestore Timestamp to a localized date string (DD.MM.YYYY).
   */
  formatTimestamp(timestamp: firestore.Timestamp | null): string {
    if (!timestamp) {
      return "–";
    }
    return timestamp.toDate().toLocaleDateString();
  }

  /** Converts a Firestore Timestamp to ISO date string for v-date-picker. */
  toIsoDate(timestamp: firestore.Timestamp | null): string | undefined {
    if (!timestamp) return undefined;
    return timestamp.toDate().toISOString().substring(0, 10);
  }

  /** Handles date selection from inline date picker, saves to Firestore. */
  async onDatePicked(
    catalog: BillingCatalog,
    field: "validFrom" | "validUntil",
    isoDate: string
  ) {
    this.$set(this.dateMenus, catalog.id + (field === "validFrom" ? "_from" : "_until"), false);
    const newTimestamp = firestore.Timestamp.fromDate(new Date(isoDate));
    try {
      await updateBillingCatalog(catalog.id, { [field]: newTimestamp });
      this.$set(catalog, field, newTimestamp);
    } catch {
      // The picker has already closed, so without this the date silently snaps back.
      SnackbarModule.showError({ key: "settings.billingCatalogs.updateError" });
    }
  }

  /** Triggers a manual billing catalog import from the Haufe X360 API. */
  async onSyncFromApi() {
    this.isSyncing = true;
    try {
      const result = await importBillingCatalogs();
      SnackbarModule.showSuccess({
        key: "settings.billingCatalogs.syncSuccess",
        params: [String(result.totalItems), String(result.catalogs.length)],
      });
      this.catalogs = await fetchBillingCatalogs();
    } catch {
      SnackbarModule.showError({ key: "settings.billingCatalogs.syncError" });
    } finally {
      this.isSyncing = false;
    }
  }

  onBack() {
    router.back();
  }

  /** Navigate to the detail view for a billing catalog. */
  openCatalog(catalog: BillingCatalog) {
    router.push({
      name: ComponentName.BILLING_CATALOG_DETAIL,
      params: { catalogId: catalog.id },
    });
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const currentPageTitle = this.$t("settings.billingCatalogs.title").toString();
    ToolbarContentModule.setToolbarTitle(baseTitle + " > " + currentPageTitle);
  }
}
